import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV } from "@/constants/testIds";

const PHONE_DISPLAY = "(705) 796-4025";
const PHONE_HREF = "tel:+17057964025";

const links = [
  { to: "/", label: "Home", id: NAV.home },
  { to: "/services", label: "Services", id: NAV.services },
  { to: "/about", label: "About", id: NAV.about },
  { to: "/contact", label: "Contact", id: NAV.contact },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className={`sticky top-0 z-40 transition-all ${scrolled ? "bg-[#E8E1D2]/90 backdrop-blur-md border-b border-[var(--line)]" : "bg-transparent"}`}>
      <div className="container-x flex items-center justify-between py-5">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
          <div className="relative w-11 h-11 rounded-full bg-[var(--ink)] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 bg-[var(--coral)] translate-y-[55%] group-hover:translate-y-[20%] transition-transform duration-500" />
            <span className="relative font-display font-bold text-[var(--cream)] text-lg leading-none">SF</span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[1.05rem] text-[var(--ink)] -mb-0.5 tracking-tight leading-tight">Smooth Finish<br/>Drywall Repairs</div>
            <div className="font-body text-[0.6rem] uppercase tracking-[0.22em] text-[var(--muted)] mt-1">Wasaga Beach, ON</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 bg-[var(--ink)] rounded-full p-1.5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  isActive ? "bg-[var(--coral)] text-[#E8E1D2]" : "text-[#E8E1D2]/70 hover:text-[#E8E1D2]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href={PHONE_HREF} data-testid={NAV.quoteCta} className="btn-coral">
            <Phone size={16} /> Call Mike for a free estimate
          </a>
        </div>

        <button onClick={() => setOpen((v) => !v)} data-testid={NAV.mobileToggle} aria-label="Toggle menu"
          className="md:hidden w-11 h-11 rounded-full bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[var(--ink)] text-[var(--cream)]">
          <div className="container-x py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.id}-mobile`}
                className={({ isActive }) =>
                  `px-3 py-3 text-xl font-display border-b border-[#E8E1D2]/10 ${isActive ? "text-[var(--coral)]" : "text-[#E8E1D2]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a href={PHONE_HREF} data-testid={`${NAV.quoteCta}-mobile`} className="btn-coral mt-4 self-start">
              <Phone size={16}/> Call Mike for a free estimate
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
